import React from 'react';
import { Chip, Typography } from '@mui/material';

/**
 * The wine-link state, in the item page header.
 *
 * THE LINE ITSELF DOES NOT CHANGE. Same text, same warning colour, same size
 * and weight, same place under the item name — for everyone, linked or not,
 * admin or not. What changes for an admin is that it now *does* something:
 * clicking it opens the link dialog.
 *
 * That is deliberate. The first attempt made the admin label read "Not linked
 * — link it" and scrolled to a panel further down the page; both were
 * rejected. A status line should read as a status line, and the remedy should
 * come to the click rather than the reader travelling to it.
 *
 * FOR A NON-ADMIN IT STAYS PURELY INFORMATIONAL — no button semantics, no
 * pointer, no dialog. Linking rewrites the item's name and is admin-only; a
 * line that invites a click and then leads to "an administrator can do this"
 * is worse than one that never offered.
 */

export interface WineLinkStatusChipProps {
  linked: boolean;
  /** Whether this user can actually perform the link. */
  isAdmin: boolean;
  /** Open the link dialog. Only wired when `isAdmin`. */
  onOpenLink?: () => void;
}

const WineLinkStatusChip: React.FC<WineLinkStatusChipProps> = ({
  linked, isAdmin, onOpenLink,
}) => {
  if (linked) {
    // Unchanged from before any of this work.
    return (
      <Typography variant="caption" color="text.secondary">
        Linked to wine catalog
      </Typography>
    );
  }

  const interactive = isAdmin && Boolean(onOpenLink);

  return (
    <Chip
      label="Not linked to wine catalog"
      size="small"
      color="warning"
      variant="outlined"
      // `clickable` supplies button semantics, keyboard focus and Enter/Space
      // activation, so the action is reachable without a mouse.
      clickable={interactive}
      onClick={interactive ? onOpenLink : undefined}
      aria-label={interactive
        ? 'Not linked to wine catalog — link to a catalog wine'
        : undefined}
      sx={{
        // The original appearance, unchanged.
        fontSize: '0.68rem',
        height: 20,
        fontWeight: 600,
        // Affordance only on hover/focus, so the resting state still reads as
        // the status line it has always been.
        ...(interactive
          ? {
            cursor: 'pointer',
            '&:hover': { backgroundColor: 'warning.main', color: 'warning.contrastText' },
            '&:focus-visible': { outline: '2px solid', outlineColor: 'warning.main' },
          }
          : {}),
      }}
    />
  );
};

export default WineLinkStatusChip;
